
import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { HelpCircle } from 'lucide-react';

interface HelpTopic {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface ContextualHelpSystemProps {
  pageId: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
}

export function ContextualHelpSystem({
  pageId,
  position = 'right',
  size = 'sm'
}: ContextualHelpSystemProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Help content organized by page ID
  const helpTopics: Record<string, HelpTopic[]> = {
    'certificates': [
      {
        id: 'overview',
        title: 'Certificate Management',
        content: (
          <div className="space-y-2">
            <p>
              This page allows you to manage certificates, track their status, and 
              ensure regulatory compliance.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Use the tabs to navigate between different certificate management functions</li>
              <li>Import certificate numbers before assigning them to students</li>
              <li>Export certificates for reporting or backup purposes</li>
            </ul>
          </div>
        )
      }
    ],
    'certificate-tracking': [
      {
        id: 'tracking',
        title: 'Certificate Tracking Dashboard',
        content: (
          <div className="space-y-2">
            <p>
              The tracking dashboard helps you monitor certificate status and identify 
              discrepancies that need attention.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>View certificates by state, year, and quarter</li>
              <li>Check for unreported or unverified certificates</li>
              <li>Resolve discrepancies before regulatory reporting</li>
            </ul>
          </div>
        )
      }
    ],
    'verification': [
      {
        id: 'verification',
        title: 'Certificate Verification',
        content: (
          <div className="space-y-2">
            <p>
              The verification process ensures certificates are properly reported 
              to regulatory authorities.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Review unverified certificates and check their details</li>
              <li>Add verification notes for audit purposes</li>
              <li>Batch verify multiple certificates when appropriate</li>
            </ul>
          </div>
        )
      }
    ],
    'reports': [
      {
        id: 'reports',
        title: 'Compliance Reports',
        content: (
          <div className="space-y-2">
            <p>
              Generate formatted reports for submission to regulatory authorities.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Select state, year, and quarter for the report</li>
              <li>Preview report data before generation</li>
              <li>Track report submissions in the history section</li>
            </ul>
          </div>
        )
      }
    ],
    'export': [
      {
        id: 'export',
        title: 'Certificate Export',
        content: (
          <div className="space-y-2">
            <p>
              Export certificate data for reporting, analysis, or backup purposes.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Apply filters to export specific certificate sets</li>
              <li>Choose from CSV, Excel, or Regulatory formats</li>
              <li>Include all necessary data for compliance purposes</li>
            </ul>
          </div>
        )
      }
    ],
    'retention': [
      {
        id: 'retention',
        title: 'Retention Management',
        content: (
          <div className="space-y-2">
            <p>
              Manage certificate retention according to regulatory requirements.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Set retention periods based on state regulations</li>
              <li>Archive certificates that exceed retention periods</li>
              <li>Maintain audit trail of archiving activities</li>
            </ul>
          </div>
        )
      }
    ]
  };
  
  const topics = helpTopics[pageId] || [];
  
  if (topics.length === 0) {
    return null;
  }
  
  const sizeClasses = {
    sm: 'w-72',
    md: 'w-80',
    lg: 'w-96'
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label="Get help"
        >
          <HelpCircle className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`${sizeClasses[size]} p-0`}
        align={position === 'left' ? 'start' : position === 'right' ? 'end' : 'center'}
        side={position === 'top' ? 'top' : position === 'bottom' ? 'bottom' : 'right'}
      >
        <div className="space-y-4 p-4">
          {topics.map(topic => (
            <div key={topic.id} className="space-y-2">
              <h4 className="font-medium text-sm">{topic.title}</h4>
              <div className="text-xs text-muted-foreground">
                {topic.content}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
